/**
 * SessionManager - Manages pre-signed URL sessions in sessionStorage
 *
 * Each browser tab has its own isolated session, allowing multiple
 * independent sessions (e.g., different organisations) in different tabs.
 */

export interface TabSession {
  userId: number;
  organisationId: number;
  organisationName: string;
  sessionId: string;
  apiToken: string;
  expiresAt: number;
}

const STORAGE_KEY = "pillar_session";
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry

class SessionManager {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Initialize session from page props (called on initial page load)
   */
  initialize(session: TabSession): void {
    this.store(session);
    this.scheduleTokenRefresh(session);
  }

  /**
   * Store session data in sessionStorage
   */
  // eslint-disable-next-line class-methods-use-this
  store(session: TabSession): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to store session:", error);
    }
  }

  /**
   * Get current session data
   */
  // eslint-disable-next-line class-methods-use-this
  getSession(): TabSession | null {
    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to get session:", error);
      return null;
    }
  }

  /**
   * Get the API token for AJAX calls
   */
  getToken(): string | null {
    return this.getSession()?.apiToken ?? null;
  }

  /**
   * Get the session ID
   */
  getSessionId(): string | null {
    return this.getSession()?.sessionId ?? null;
  }

  /**
   * Get the current organisation ID
   */
  getOrganisationId(): number | null {
    return this.getSession()?.organisationId ?? null;
  }

  /**
   * Check if session is valid and not expired
   */
  isValid(): boolean {
    const session = this.getSession();
    if (!session) return false;

    const now = Date.now();
    const expiresAt = session.expiresAt * 1000; // Convert to milliseconds
    return now < expiresAt;
  }

  /**
   * Check if session is about to expire
   */
  isExpiringSoon(): boolean {
    const session = this.getSession();
    if (!session) return true;

    const now = Date.now();
    const expiresAt = session.expiresAt * 1000;
    return (expiresAt - now) < TOKEN_REFRESH_THRESHOLD;
  }

  /**
   * Clear session data
   */
  clear(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Update session with new token
   */
  updateToken(apiToken: string, expiresAt: number): void {
    const session = this.getSession();
    if (session) {
      session.apiToken = apiToken;
      session.expiresAt = expiresAt;
      this.store(session);
      this.scheduleTokenRefresh(session);
    }
  }

  /**
   * Schedule automatic token refresh before expiry
   */
  private scheduleTokenRefresh(session: TabSession): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const now = Date.now();
    const expiresAt = session.expiresAt * 1000;
    const refreshAt = expiresAt - TOKEN_REFRESH_THRESHOLD;
    const delay = Math.max(refreshAt - now, 0);

    if (delay > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken();
      }, delay);
    }
  }

  /**
   * Refresh the API token
   */
  async refreshToken(): Promise<boolean> {
    const session = this.getSession();
    if (!session) return false;

    try {
      const response = await fetch("/api/presigned_sessions/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.apiToken}`,
          "X-Session-Id": session.sessionId,
        },
        body: JSON.stringify({
          session_id: session.sessionId,
        }),
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      this.updateToken(data.api_token, new Date(data.expires_at).getTime() / 1000);
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to refresh token:", error);
      return false;
    }
  }

  /**
   * Request a new session for a different organisation
   */
  async requestNewSession(
    organisationId: number,
    options: { path?: string | undefined; newWindow?: boolean | undefined } = {}
  ): Promise<string | null> {
    const session = this.getSession();
    if (!session) return null;

    try {
      const response = await fetch("/api/presigned_sessions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.apiToken}`,
          "X-Session-Id": session.sessionId,
        },
        body: JSON.stringify({
          organisation_id: organisationId,
          path: options.path || "/dashboard",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to request new session");
      }

      const data = await response.json();

      if (options.newWindow) {
        window.open(data.url, "_blank");
      }

      return data.url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to request new session:", error);
      return null;
    }
  }

  /**
   * Open organisation in new tab
   */
  async openInNewTab(organisationId: number, path?: string): Promise<void> {
    await this.requestNewSession(organisationId, { path, newWindow: true });
  }

  /**
   * Switch organisation in current tab
   */
  async switchOrganisation(organisationId: number, path?: string): Promise<void> {
    const url = await this.requestNewSession(organisationId, { path, newWindow: false });
    if (url) {
      window.location.href = url;
    }
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();

// Export class for testing
export { SessionManager };
